import { arLayerModelOf, arLayerOf, arSceneIdOf, arTargetKey } from "./arKeys.ts"; // 🔴 확장자 — scripts/seed-magic.mjs 가 node 로 직접 읽는다(build-ar-magic 과 같다)
import { normalizeArInteract } from "./arInteract.ts";
import { toRelativeEdgeAsset } from "./assetUrl.ts";

/**
 * 🪄 **저장소에만 있는 마법을 아이 화면으로** — 무엇을 올릴지 정하는 판정, 여기 한 곳.
 *
 * ## 왜 필요한가
 * 마법 장면을 만들어 배포해도 **아이 화면에는 안 나온다.** 아이 화면이 읽는 자리 목록은
 * 「정적 폴백 위에 게시본을 덮은 것」이고, 덮기는 **항목 통째로** 게시본이 이긴다. 그 쪽 타깃은
 * 이미 게시돼 있으니(`.mind` 주소 때문에) 저장소가 얹어 둔 `interact` 가 통째로 가려진다.
 * 그래서 게시본에도 **넣어 줘야** 한다 — 그 일이 이 계획이다.
 *
 * ## 🔴 왜 shared 인가
 * 이 일을 하는 길이 **둘**이다: 개발자용 `pnpm magic:seed`(명령줄·운영자 열쇠 필요)와
 * 운영자용 [✅ 출시 준비]의 단추(제 열쇠로 누른다). 규칙이 갈리면 「명령으로는 되는데
 * 단추로는 안 된다」가 된다 — 이 저장소가 책·굿즈에서 이미 겪은 모양이다.
 *
 * ## 규칙 셋
 * 1. **덮지 않는다.** 게시본에 이미 `interact` 가 있으면 그 장면은 **통째로** 건너뛴다
 *    (운영자가 관리자에서 만든 것일 수 있다).
 * 2. **반쪽으로 넣지 않는다.** 정의를 건너뛰는 장면은 **그 층 그림도** 안 건드린다.
 *    이 저장소에서 그림과 자리는 같이 설계된다 — 배경만 새것이면 유물이 진열장 밖에 뜬다
 *    (2026-09-09 실제로 그랬다).
 * 3. **운영자가 올린 그림은 그대로 둔다.** 저장소 그림은 `/art/…`, 운영자가 올린 것은
 *    `/assets/…`(R2) 다. 주소가 다르면 운영자 것을 이긴 것으로 보지 않는다.
 */

/** 자리 하나 — 매니페스트 항목에서 이 판정이 보는 것만. */
export interface MagicEntry {
  url?: string;
  interact?: unknown;
  fps?: number;
  once?: boolean;
  [k: string]: unknown;
}
export type MagicAssets = Record<string, MagicEntry>;

export interface MagicScenePlan {
  sceneId: string;
  layers: number;
  hotspots: number;
  imageKeys: string[];
  /**
   * 📷 **카메라 인식 파일(.mind)만 채운다** — 마법 정의는 게시본과 같은데 게시본 타깃 주소가 비어 있고
   * 저장소에 정적 `.mind` 가 있을 때(`scripts/compile-mind.mjs` 로 만든 것). 이게 없으면 「이미 그대로 있어요」로
   * 건너뛰어 카메라가 영영 안 켜진다(2026-09-12 · 7개 장면이 그랬다).
   */
  camera?: true;
}

export interface MagicPublishPlan {
  /** 게시본에 **없어서** 넣을 장면들 — 그냥 넣으면 된다(잃을 것이 없다). */
  add: MagicScenePlan[];
  /**
   * 게시본에도 있는데 **저장소 것과 다른** 장면들.
   *
   * 🔴 자동으로 안 덮는다 — 운영자가 관리자에서 고친 것일 수 있다. 대신 **한 장면씩 골라서**
   * 바꾸게 한다(`force`). 저장소에서 자리를 고쳤을 때 이 길이 없으면, 고친 것이 아이에게
   * 영영 안 닿는다(2026-09-09 전시관 자리를 고치고 실제로 막혔다).
   */
  differs: MagicScenePlan[];
  /** 건드리지 않는 장면과 그 이유 — 화면이 그대로 보여 준다(「왜 안 올라가지」를 없앤다). */
  keep: { sceneId: string; why: string }[];
}

/** 저장소 그림인가(`/art/…`). 운영자가 올린 것은 `/assets/…` 라 여기 안 든다. */
function isRepoArt(url: unknown): boolean {
  return typeof url === "string" && url.startsWith("/art/");
}

/**
 * 무엇을 올릴지 — **저장소(정적 폴백)와 게시본을 견주어** 정한다.
 *
 * @param repo 저장소가 아는 자리 목록(`content/asset-manifest.json`, `pnpm content:build` 가 만든다)
 * @param live 지금 게시본(`/api/asset-manifest`)
 * @param force 게시본에 있어도 **저장소 것으로 바꿀** 장면들(운영자가 한 장면씩 고른다)
 */
export function magicPublishPlan(repo: MagicAssets, live: MagicAssets, force: readonly string[] = []): MagicPublishPlan {
  const add: MagicScenePlan[] = [];
  const differs: MagicScenePlan[] = [];
  const keep: MagicPublishPlan["keep"] = [];
  const forced = new Set(force);

  for (const key of Object.keys(repo)) {
    if (!key.startsWith("ar.target.")) continue;
    const def = normalizeArInteract(repo[key]?.interact);
    const sceneId = arSceneIdOf(key);
    if (!sceneId) continue;
    /**
     * 📷 **마법이 없는 장면(경주 카드)도 카메라 주소는 채운다** (2026-09-14).
     * 카드 다섯 장의 인식 파일을 저장소에 구웠는데, 첨성대는 게시본에 **빈 주소**(`{ url: "" }`)로 항목이 있어
     * 항목 통째로 게시본이 이긴다 — 저장소 파일이 영영 안 간다. 그런데 이 계획은 마법이 있는 장면만 봐서
     * 운영자에게 **채울 단추가 없었다**(「만들어 놓고 닿을 길이 없다」).
     * 🔴 좁게 한다: 게시본에 **항목이 있고 주소가 비었을 때만**. 항목이 아예 없으면 정적 폴백이 이미 가고,
     *    운영자가 올린 `.mind` 가 있으면 절대 안 덮는다.
     */
    if (!def) {
      const liveEntry = live[key];
      if (liveEntry && !liveEntry.url && isRepoArt(repo[key]?.url)) {
        add.push({ sceneId, layers: 0, hotspots: 0, imageKeys: [], camera: true });
      }
      continue;
    }

    const liveDef = normalizeArInteract(live[key]?.interact);
    const same = liveDef !== null && JSON.stringify(liveDef) === JSON.stringify(def);
    // 📷 정의는 같은데 카메라 인식 파일만 빠졌다 — 저장소 것으로 채운다(운영자가 올린 .mind 는 절대 안 덮는다)
    const cameraOnly = same && !live[key]?.url && isRepoArt(repo[key]?.url);
    // ① 덮지 않는다 — 똑같으면 할 일이 없고, 다르면 **고르게** 한다
    if (same && !cameraOnly) {
      keep.push({ sceneId, why: "이미 아이 화면에 그대로 있어요" });
      continue;
    }
    const willWrite = liveDef === null || forced.has(sceneId) || cameraOnly;

    // ② 그 장면의 층 그림 중 **저장소 것으로 바꿔야 하는** 자리
    const imageKeys: string[] = [];
    let blocked = 0;
    for (const k of Object.keys(repo)) {
      // 🧊 3D 파일(`ar.layer.model.*`)도 그 장면의 그림이다 — 빠뜨리면 카메라 AR 이 띠로만 돈다(2026-09-12 까지 그랬다)
      if ((arLayerOf(k) ?? arLayerModelOf(k))?.sceneId !== sceneId) continue;
      const want = repo[k]?.url;
      if (!isRepoArt(want)) continue;
      const cur = live[k];
      // ③ 운영자가 올린 그림은 그대로 둔다
      if (cur?.url && !isRepoArt(cur.url)) {
        blocked++;
        continue;
      }
      if (cur?.url === want && cur?.fps === repo[k]?.fps && !!cur?.once === !!repo[k]?.once) continue;
      imageKeys.push(k);
    }
    const row: MagicScenePlan = { sceneId, layers: def.layers.length, hotspots: def.hotspots.length, imageKeys, ...(cameraOnly ? { camera: true as const } : {}) };
    // 🔴 정의를 안 바꾸는 장면은 **그림도** 안 바꾼다(반쪽 금지) — `differs` 는 보여 주기만 한다
    (willWrite ? add : differs).push(row);
    if (blocked > 0 && willWrite) {
      keep.push({ sceneId, why: `층 그림 ${blocked}개는 운영자가 올린 것이라 그대로 둡니다` });
    }
  }
  return { add, differs, keep };
}

/**
 * 계획을 자리 목록에 **얹는다** — 원본을 바꾸지 않고 새 목록을 돌려준다.
 *
 * 🔴 **얹기만 한다.** 타깃 항목의 `.mind` 주소·설명(alt)을 지우면 카메라 AR 이 죽는다.
 * 그리고 계획에 없는 자리는 **한 글자도** 안 건드린다 — 게시는 통째로 바꾸기라,
 * 여기서 흘리면 운영자가 올린 수백 개가 같이 사라진다.
 */
export function applyMagicPublish(base: MagicAssets, repo: MagicAssets, plan: MagicPublishPlan): MagicAssets {
  const next: MagicAssets = { ...base };
  for (const scene of plan.add) {
    const tKey = arTargetKey(scene.sceneId);
    const interact = repo[tKey]?.interact;
    // 📷 타깃 주소는 **비어 있을 때만** 저장소 것으로 채운다 — 운영자가 카메라로 외운 .mind 를 덮으면 안 된다
    const cur = next[tKey] ?? {};
    const repoUrl = repo[tKey]?.url;
    const fill = !cur.url && isRepoArt(repoUrl) ? { url: repoUrl as string, ...(repo[tKey]?.alt ? { alt: repo[tKey]!.alt } : {}) } : {};
    if (!interact) {
      // 📷 마법 없는 카드 — 카메라 주소만 채운다(얹을 층 그림도 없다). 채울 것이 없으면 손대지 않는다
      if (Object.keys(fill).length > 0) next[tKey] = { ...cur, ...fill };
      continue;
    }
    next[tKey] = { ...cur, ...fill, interact };
    for (const k of scene.imageKeys) {
      const src = repo[k];
      if (!src?.url) continue;
      // fps·once 는 그림 쪽 값이다 — 없으면(정지 그림) 예전 값을 지운다
      const { fps: _f, once: _o, ...keepRest } = next[k] ?? {};
      next[k] = {
        ...keepRest,
        url: src.url,
        ...(src.fps ? { fps: src.fps } : {}),
        ...(src.once ? { once: true } : {}),
      };
    }
  }
  return next;
}

/**
 * 🖼️ **운영자가 올린 층 그림 자리에 「저장소 그림으로 바꾸기」를 내밀까** — 내밀면 무엇으로.
 *
 * ## 🔴 왜 필요한가 (2026-09-10 · 22쪽 팥 꼬투리)
 * 운영자가 올린 **685KB PNG** 가 저장소의 **209KB WebP 와 픽셀까지 같은 그림**이었다(평균 차이 0.25/255).
 * 교실 회선에서 3.6초 빈 칸 → 1.1초. 그런데 관리자에서 바꿀 길이 없었다:
 *  · [저장소 것으로 바꾸기](게시 카드)는 **운영자가 올린 것은 안 덮는다** — 일부러 그렇게 만들었다
 *  · 층 옆 안내는 「[지우기]를 누르면 저장소 그림이 다시 보인다」고 했는데 **거짓이었다** —
 *    [지우기]는 층을 장면에서 **통째로** 지운다(누르는 자리·손잡이까지). 누르면 놀이가 깨진다
 *  · 남은 길은 「파일을 받아서 다시 올리기」뿐 — 이 저장소가 「다시 올리라면 안 한다」로 배운 길이다
 *
 * 그래서 **운영자가 한 층씩 골라** 저장소 그림으로 바꾸게 한다. 자동으로는 안 한다(올린 데는 이유가 있다).
 *
 * 내미는 조건: 지금 그림이 **운영자가 올린 것**(`/assets/…`)이고, 저장소에 같은 자리의 그림(`/art/…`)이
 * 따로 있을 때. 바꿀 때는 저장소의 **재생 값(fps·once)도 함께** 가져온다 — 띠인데 fps 를 빼먹으면
 * 기본 속도로 돌거나 한 번만 돌아야 할 것이 계속 돈다.
 */
/** 층 그림 자리에서 이 판정이 보는 것만 — 관리자·검사가 쓰는 매니페스트 항목 모양이 조금씩 달라 넓게 받는다. */
interface LayerArtEntry {
  url?: string;
  fps?: number;
  once?: boolean;
}

export function repoArtOffer<E extends LayerArtEntry>(
  current: E | undefined,
  repo: LayerArtEntry | undefined,
): (E & { url: string }) | null {
  /*
   * 🔴 **관리자는 주소를 절대형으로 들고 있다**(`https://…/assets/…` — 미리보기가 열리게, contentApi `absArtUrl`).
   * 상대형(`/assets/…`)만 알아보던 때는 운영 관리자에서 **단추가 한 번도 안 떴다**(2026-09-11) —
   * 검사는 상대형으로 넣어서 초록이었다. 그래서 여기서 **어느 형태로 와도** 상대형으로 읽는다.
   */
  const now = toRelativeEdgeAsset(current?.url);
  const want = repo?.url;
  if (typeof now !== "string" || !now.startsWith("/assets/")) return null; // 운영자가 올린 것만
  if (!isRepoArt(want) || want === now) return null;
  const { url: _u, fps: _f, once: _o, ...keep } = current!;
  return {
    ...keep, // alt 등 운영자가 적은 것은 남긴다
    url: want as string,
    ...(typeof repo?.fps === "number" ? { fps: repo.fps } : {}),
    ...(repo?.once === true ? { once: true } : {}),
  } as E & { url: string };
}
